import React, { FC, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { OnboardingStatus, GetOnboardingStatusResponse } from '@dreamai/types';
import { ReactComponent as DreamLogoIcon } from '../../icons/dream-logo-icon.svg';
import { ReactComponent as DreamLogoWordmark } from '../../icons/dream-logo-wordmark.svg';
import { ReactComponent as LogoutIcon } from '../../icons/logout.svg';
import api from '../../api';
import PersonalDetailsStep from './steps/PersonalDetailsStep';
import ConnectAccountsStep from './steps/ConnectAccountsStep';
import SetPersonalityStep from './steps/SetPersonalityStep';
import ChooseProfileStep from './steps/ChooseProfileStep';
import MeetAssistantStep from './steps/MeetAssistantStep';
import AssistantSetupStep from './steps/AssistantSetupStep';
import EmailIntroStep from './steps/EmailIntroStep';
import CardIntroStep from './steps/CardIntroStep';
import SuggestionsStep from './steps/SuggestionsStep';
import OnboardingProgressBar from './OnboardingProgressBar';
import './onboarding-flow.scss';

export type OnboardingStep =
  | 'personal-details'
  | 'connect-accounts'
  | 'set-personality'
  | 'choose-profile'
  | 'meet-assistant'
  | 'assistant-setup'
  | 'email-intro'
  | 'task-intro'
  | 'suggestions';

/**
 * Determines the first incomplete step based on onboarding status.
 */
function getFirstIncompleteStep(status: OnboardingStatus): OnboardingStep {
  if (!status.personalDetailsCompleted) return 'personal-details';
  if (!status.candidateSelected) return 'set-personality';
  if (!status.paCreationCompleted) return 'meet-assistant';
  if (!status.assistantSetupCompleted) return 'assistant-setup';
  if (!status.permissionsCompleted) return 'connect-accounts';
  if (!status.contactStepCompleted) return 'email-intro';
  if (!status.taskIntroCompleted) return 'task-intro';
  if (!status.suggestionsCompleted) return 'suggestions';
  return 'suggestions';
}

const OnboardingFlow: FC = () => {
  const { getAccessTokenSilently, user, logout } = useAuth0();
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Client-side sub-step for the PA creation phase
  // (set-personality → choose-profile is not backed by DB state)
  const [paSubStep, setPaSubStep] = useState<
    'set-personality' | 'choose-profile'
  >(
    sessionStorage.getItem('onboarding_personality')
      ? 'choose-profile'
      : 'set-personality',
  );

  // Workflow ID for the email provisioning step (passed from MeetAssistant → AssistantSetup)
  // Persisted in sessionStorage so the Pusher listener can match the event.
  const [emailWorkflowId, setEmailWorkflowId] = useState<string | null>(() =>
    sessionStorage.getItem('onboarding_email_workflow_id'),
  );

  const handleSetupStarted = useCallback((wfId: string | null) => {
    setEmailWorkflowId(wfId);
    if (wfId) {
      sessionStorage.setItem('onboarding_email_workflow_id', wfId);
    } else {
      sessionStorage.removeItem('onboarding_email_workflow_id');
    }
  }, []);

  // Current step derived from backend status + local sub-steps
  const baseStep = status ? getFirstIncompleteStep(status) : null;
  const currentStep = (() => {
    if (!baseStep) return null;
    if (baseStep === 'set-personality') return paSubStep;
    return baseStep;
  })();

  const goToChooseProfile = useCallback(() => {
    setPaSubStep('choose-profile');
  }, []);

  const goBackToPersonality = useCallback(() => {
    sessionStorage.removeItem('onboarding_personality');
    setPaSubStep('set-personality');
  }, []);

  /**
   * Fetch onboarding status from the backend
   */
  const fetchStatus = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetOnboardingStatusResponse>(
        '/onboarding/status',
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        const s = response.data.status;
        setStatus(s);

        // If onboarding is fully complete, navigate to main app
        if (s.onboardingCompleted) {
          navigate('/', { replace: true });
          return;
        }
      }
    } catch (err) {
      console.error('Error fetching onboarding status:', err);
      setError('Failed to load onboarding status');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, navigate]);

  useEffect(() => {
    fetchStatus();
  }, []);

  /**
   * Refresh status — the step will automatically advance
   * based on the new status from the backend.
   */
  const refreshStatus = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetOnboardingStatusResponse>(
        '/onboarding/status',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        const s = response.data.status;
        setStatus(s);
        if (s.onboardingCompleted) {
          navigate('/', { replace: true });
        }
      }
    } catch (err) {
      console.error('Error refreshing onboarding status:', err);
    }
  }, [getAccessTokenSilently, navigate]);

  const stepProps: OnboardingStepProps = {
    status,
    refreshStatus,
  };

  if (loading) {
    return (
      <div className='onboarding-flow'>
        <div className='onboarding-loading'>
          <div className='spinner' />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='onboarding-flow'>
        <div className='onboarding-error'>
          <p>{error}</p>
          <button onClick={fetchStatus}>Try again</button>
        </div>
      </div>
    );
  }

  const isWideStep =
    currentStep === 'assistant-setup' ||
    currentStep === 'connect-accounts' ||
    currentStep === 'meet-assistant' ||
    currentStep === 'email-intro' ||
    currentStep === 'task-intro' ||
    currentStep === 'suggestions';
  const hasSidebar =
    currentStep === 'assistant-setup' ||
    currentStep === 'connect-accounts' ||
    currentStep === 'email-intro' ||
    currentStep === 'task-intro' ||
    currentStep === 'suggestions';

  const assistantImage = hasSidebar
    ? status?.assistantImageUrl ||
      sessionStorage.getItem('onboarding_assistant_image')
    : null;

  return (
    <div className={`onboarding-flow${hasSidebar ? ' has-sidebar' : ''}`}>
      {hasSidebar && (
        <div className='onboarding-sidebar'>
          {assistantImage && (
            <div className='sidebar-image-wrapper'>
              <img
                src={assistantImage}
                alt='Your assistant'
                className='sidebar-image'
              />
            </div>
          )}
        </div>
      )}
      <div className={`onboarding-content${isWideStep ? ' wide-content' : ''}`}>
        <header className='onboarding-header'>
          <div className='dream-logo'>
            <DreamLogoIcon className='dream-logo-icon' />
            <DreamLogoWordmark className='dream-logo-wordmark' />
          </div>
          <div className='header-right'>
            {user?.email && <span className='header-email'>{user.email}</span>}
            <button
              className='header-logout'
              onClick={handleLogout}
              aria-label='Log out'
            >
              <LogoutIcon />
            </button>
          </div>
        </header>
        {currentStep &&
          [
            'assistant-setup',
            'connect-accounts',
            'email-intro',
            'task-intro',
            'suggestions',
          ].includes(currentStep) && (
            <OnboardingProgressBar
              currentStep={
                currentStep === 'assistant-setup'
                  ? 1
                  : currentStep === 'connect-accounts'
                    ? 2
                    : currentStep === 'email-intro'
                      ? 3
                      : currentStep === 'task-intro'
                        ? 4
                        : 5
              }
              totalSteps={5}
            />
          )}
        {currentStep === 'personal-details' && (
          <PersonalDetailsStep {...stepProps} />
        )}
        {currentStep === 'set-personality' && (
          <SetPersonalityStep {...stepProps} onComplete={goToChooseProfile} />
        )}
        {currentStep === 'choose-profile' && (
          <ChooseProfileStep {...stepProps} onGoBack={goBackToPersonality} />
        )}
        {currentStep === 'meet-assistant' && (
          <MeetAssistantStep
            {...stepProps}
            onSetupStarted={handleSetupStarted}
          />
        )}
        {currentStep === 'assistant-setup' && (
          <AssistantSetupStep {...stepProps} workflowId={emailWorkflowId} />
        )}
        {currentStep === 'connect-accounts' && (
          <ConnectAccountsStep {...stepProps} />
        )}
        {currentStep === 'email-intro' && (
          <EmailIntroStep
            {...stepProps}
            assistantName={status?.assistantName ?? null}
            assistantNickname={status?.assistantNickname ?? null}
            assistantEmail={status?.assistantEmail ?? null}
            assistantEmailReady={status?.assistantEmailReady ?? false}
            assistantSignOff={status?.assistantSignOff ?? null}
            userName={status?.userName ?? null}
          />
        )}
        {currentStep === 'task-intro' && (
          <CardIntroStep
            {...stepProps}
            assistantNickname={status?.assistantNickname ?? null}
            assistantName={status?.assistantName ?? null}
            assistantPredominantColour={
              status?.assistantPredominantColour ?? null
            }
            userName={status?.userName ?? null}
          />
        )}
        {currentStep === 'suggestions' && (
          <SuggestionsStep
            {...stepProps}
            assistantId={status?.assistantId ?? null}
            assistantName={status?.assistantName ?? null}
            assistantNickname={status?.assistantNickname ?? null}
            assistantImageUrl={status?.assistantImageUrl ?? null}
            assistantPredominantColour={
              status?.assistantPredominantColour ?? null
            }
          />
        )}
      </div>
    </div>
  );
};

export default OnboardingFlow;

export interface OnboardingStepProps {
  status: OnboardingStatus | null;
  refreshStatus: () => Promise<void>;
}

export interface SetPersonalityStepProps extends OnboardingStepProps {
  onComplete: () => void;
}

export interface ChooseProfileStepProps extends OnboardingStepProps {
  onGoBack: () => void;
}
